import { useEffect, useRef } from 'react'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'

import { useAuth } from '@/lib/auth'
import { subscribeToBrowserEvent } from '@/lib/signalr/connection-manager'

import { browserEventKey, type BrowserEvent } from './browser-event'

export function useRefreshQueriesOn(
  event: BrowserEvent<unknown[]>,
  queryKeys: readonly QueryKey[],
  coalesceMs = 0,
): void {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const eventRef = useRef(event)
  const queryKeysRef = useRef(queryKeys)
  useEffect(() => {
    eventRef.current = event
    queryKeysRef.current = queryKeys
  })

  const key = browserEventKey(event)
  useEffect(() => {
    if (!isAuthenticated) return
    if (coalesceMs === 0) {
      const refresh = () => {
        for (const queryKey of queryKeysRef.current)
          void queryClient.invalidateQueries({ queryKey })
      }
      return subscribeToBrowserEvent(eventRef.current, refresh, refresh)
    }
    let timer: ReturnType<typeof setTimeout> | undefined
    let running = false
    let dirty = false
    let disposed = false
    const refresh = () => {
      dirty = true
      if (running || timer !== undefined || disposed) return
      timer = setTimeout(() => { void run() }, coalesceMs)
    }
    const run = async () => {
      timer = undefined
      running = true
      dirty = false
      try {
        await Promise.all(queryKeysRef.current.map(async (queryKey) => {
          // Cancel even the first fetch: it may have read state before the notice.
          await queryClient.cancelQueries({ queryKey })
          if (!disposed) await queryClient.invalidateQueries({ queryKey })
        }))
      } finally {
        running = false
        if (dirty) refresh()
      }
    }
    const unsubscribe = subscribeToBrowserEvent(eventRef.current, refresh, refresh, undefined, true)
    return () => {
      disposed = true
      unsubscribe()
      clearTimeout(timer)
    }
  }, [isAuthenticated, key, queryClient, coalesceMs])
}
